import { FC } from 'react';
import { Routes } from '~/common/constants/routes';
import { TrackingListId } from '~/types/tracking';
import { trackSelectItem } from '~/common/utils/tracking';
import { shopifySrcSet } from '~/common/utils/image';
import { formatPriceOrFree } from '~/common/utils/currency';
import { isOneVariantAvailable } from '~/common/utils/product';
import { Product } from '~/types/product';
import {
  TileFreeBadge,
  TileFreeBadgeLead,
  TileFreeBadgeSub,
  TileImage,
  TileImageWrap,
  TileLink,
  TilePrice,
  TileSoldOutBadge,
  TileTitle,
} from './styles';

/** Same label `ProductsSection` hands `ProductCard`. */
const SOLD_OUT_LABEL = 'SOLD OUT';

/** Tiles above this index load lazily. A drop shows eight, so eight is the whole grid. */
const EAGER_TILES = 8;

interface ScratchTileProps {
  product: Product;
  index: number;
  listId: TrackingListId;
}

/**
 * One product in the scratch drop grid.
 *
 * A deliberate second copy of the shape `ProductCard` renders, not a reuse of it.
 * `ProductCard` carries a hover front/back image swap with an `Image()` prefetch, a
 * `TextContainer` that switches from absolute to static below `lg`, a glow-in-the-dark
 * badge and an `ImageWrapper` that `scale(0.7)`s above 1280px — all of which fight a tile
 * roughly 165px wide on desktop and ~75px on mobile. The pieces that carry real logic
 * (`shopifySrcSet`, `trackSelectItem`) are imported rather than rewritten.
 *
 * A sold-out product renders dimmed with a "SOLD OUT" badge in place of the free badge,
 * as `ProductCard` does with its overlay. It stays a link: the PDP is where the shopper
 * learns which sizes went, and `ProductCard` links its sold-out state too.
 */
export const ScratchTile: FC<ScratchTileProps> = ({
  product,
  index,
  listId,
}) => {
  const { title, prices, images, handle } = product;

  const image = images[0];
  const soldOut = !isOneVariantAvailable(product.variants);

  // `Number.isFinite`, never a truthiness check: `Number(undefined)` is `NaN`, which is
  // falsy, so a product whose price failed to load would otherwise be advertised as FREE.
  // (`Price.tsx:23` has exactly that bug site-wide; it is not this component's to fix.)
  const maxPrice = Number(prices.maxPrice);
  const isFree = Number.isFinite(maxPrice) && maxPrice === 0;

  // The mockup shows `£ 00.00` on free tiles, not the word "Free" that `ProductPrice`
  // renders — the badge over the image already says FREE, so the price row repeating it
  // would be redundant. Formatting through `formatCurrency`'s display sibling keeps the
  // symbol correct for the presentment currency rather than hardcoding a pound sign.
  //
  // `maxPrice` is passed through unguarded on purpose: `getFormattedCurrency` answers ''
  // for NaN, so an unresolvable price renders an EMPTY price row. Coalescing to 0 here
  // would print "£0.00" and advertise a broken product as free.
  const price = formatPriceOrFree(maxPrice, prices.currencyCode);

  return (
    <TileLink
      to={`${Routes.Product}/${handle}`}
      onClick={() => trackSelectItem(product, listId)}
    >
      <TileImageWrap $soldOut={soldOut}>
        {image ? (
          <TileImage
            src={`${image}&width=600&format=auto&quality=100`}
            srcSet={shopifySrcSet(image)}
            sizes="(max-width: 600px) 25vw, 180px"
            loading={index < EAGER_TILES ? 'eager' : 'lazy'}
            alt={`${title} - Broken Planet sustainable streetwear`}
          />
        ) : null}

        {/* Announced, not aria-hidden: this is the only place a shopper is told the item
            is free with a purchase. The price row renders the formatted zero, which reads
            as a number, not as a condition. */}
        {soldOut ? (
          <TileFreeBadge>
            <TileSoldOutBadge>{SOLD_OUT_LABEL}</TileSoldOutBadge>
          </TileFreeBadge>
        ) : isFree ? (
          <TileFreeBadge>
            <TileFreeBadgeLead>Free</TileFreeBadgeLead>
            <TileFreeBadgeSub>With purchase</TileFreeBadgeSub>
          </TileFreeBadge>
        ) : null}
      </TileImageWrap>

      {/* Hidden below `sm`, per the mobile design: at a ~75px tile there is nowhere to
          put it. Rendered rather than omitted so the accessible name survives. */}
      <TileTitle>{title}</TileTitle>
      <TilePrice>{price}</TilePrice>
    </TileLink>
  );
};
